export const onlyDigits = (v = '') => String(v).replace(/\D/g, '');

export const formatCNPJ = (v = '') => {
  const d = onlyDigits(v).slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

export const formatCEP = (v = '') => {
  const d = onlyDigits(v).slice(0, 8);
  return d.replace(/^(\d{5})(\d)/, '$1-$2');
};

export const formatPhone = (v = '') => {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 10) {
    return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  }
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
};

export const formatBRL = (v) => {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export const formatBRLInput = (v = '') => {
  const d = onlyDigits(v);
  if (!d) return '';
  const n = Number(d) / 100;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const parseBRLInput = (v = '') => {
  const d = onlyDigits(v);
  return d ? Number(d) / 100 : 0;
};

// PTAX/cotacao: 4 casas decimais (padrao BACEN). Mesma logica do BRL
// mas com escala 10000. Ex: digitar "57423" mostra "5,7423".
export const formatPtaxInput = (v = '') => {
  const d = onlyDigits(v);
  if (!d) return '';
  const n = Number(d) / 10000;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
};

export const parsePtaxInput = (v = '') => {
  const d = onlyDigits(v);
  return d ? Number(d) / 10000 : 0;
};

export const formatDateBR = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export const todayISO = () => new Date().toISOString().slice(0, 10);

export const monthYearBR = (iso) => {
  if (!iso) return '—';
  const [y, m] = iso.split('-');
  const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${meses[Number(m) - 1]}/${y}`;
};

const MESES_LONG = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

export const monthYearLongBR = (iso) => {
  if (!iso) return '—';
  const [y, m] = iso.split('-');
  return `${MESES_LONG[Number(m) - 1]} de ${y}`;
};

export const formatDateLongBR = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${Number(d)} de ${MESES_LONG[Number(m) - 1]} de ${y}`;
};

export const validateCNPJ = (v = '') => {
  const c = onlyDigits(v);
  if (c.length !== 14 || /^(\d)\1+$/.test(c)) return false;
  const calc = (slice) => {
    let sum = 0;
    let pos = slice.length - 7;
    for (let i = slice.length; i >= 1; i--) {
      sum += Number(slice[slice.length - i]) * pos--;
      if (pos < 2) pos = 9;
    }
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const d1 = calc(c.slice(0, 12));
  const d2 = calc(c.slice(0, 13));
  return d1 === Number(c[12]) && d2 === Number(c[13]);
};

export const formatCPF = (v = '') => {
  const d = onlyDigits(v).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

export const validateCPF = (v = '') => {
  const c = onlyDigits(v);
  if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
  const calc = (len) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(c[i]) * (len + 1 - i);
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return calc(9) === Number(c[9]) && calc(10) === Number(c[10]);
};

export const validateEmail = (v = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export const validateEmailList = (v = '') =>
  v.split(',').map(e => e.trim()).filter(Boolean).every(validateEmail);

export const uid = () => Math.random().toString(36).slice(2, 10).toUpperCase();

/**
 * Converte um ID do Google Drive em URL embed-able.
 * Aceita também URL completa (passa adiante intacta).
 * Compatível com qualquer arquivo publico no Drive.
 */
export const driveImageUrl = (idOrUrl) => {
  if (!idOrUrl) return '';
  const s = String(idOrUrl).trim();
  if (!s) return '';
  // Ja e URL completa
  if (/^https?:\/\//i.test(s)) {
    // Tentar extrair ID de URLs do Drive (ex: drive.google.com/file/d/ID/view, .../uc?id=ID, .../open?id=ID)
    const m = s.match(/[?&]id=([a-zA-Z0-9_-]{20,})/) || s.match(/\/file\/d\/([a-zA-Z0-9_-]{20,})/);
    if (m) return `https://lh3.googleusercontent.com/d/${m[1]}`;
    return s;
  }
  // ID puro — Drive retorna a imagem direto via lh3.googleusercontent.com/d/ID
  return `https://lh3.googleusercontent.com/d/${s}`;
};
